import React, { forwardRef, useEffect, useRef, useState } from 'react';
import DatePicker from '../DatePicker';
import { ignoreNextTimeCommit } from '../DatePicker/TimeFields';
import RecurringFields from './RecurringFields';
import './DateTimePopover.css';

/** Panel title for a mission date field ("Due:" → "Due date"). */
export function dateFieldTitle(label) {
  const name = String(label || '').replace(/:$/, '').trim();
  if (/^due$/i.test(name)) return 'Due date';
  return name || 'Set date';
}

function digitsOnly(raw, maxLen) {
  return String(raw || '').replace(/\D/g, '').slice(0, maxLen);
}

function formatCountdownTarget(date) {
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CountdownFields({ commits, applyLabel, onApply }) {
  const [days, setDays] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const applyRef = useRef(onApply);
  const touchedRef = useRef(false);
  applyRef.current = onApply;

  const totalMinutes = (parseInt(days, 10) || 0) * 24 * 60
    + (parseInt(hours, 10) || 0) * 60
    + (parseInt(minutes, 10) || 0);
  const when = totalMinutes > 0
    ? new Date(Date.now() + totalMinutes * 60 * 1000)
    : null;

  useEffect(() => {
    if (commits) return undefined;
    if (totalMinutes <= 0) {
      if (touchedRef.current) applyRef.current(null);
      return undefined;
    }
    touchedRef.current = true;
    applyRef.current(new Date(Date.now() + totalMinutes * 60 * 1000));
    return undefined;
  }, [commits, totalMinutes]);

  const field = (label, value, setValue, autoFocus) => (
    <label className="date-time-popover__countdown-field">
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder="0"
        value={value}
        aria-label={label}
        onChange={(e) => setValue(digitsOnly(e.target.value, 4))}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <div className="date-time-popover__countdown">
      <div className="date-time-popover__countdown-row">
        {field('Days', days, setDays, true)}
        {field('Hours', hours, setHours, false)}
        {field('Min', minutes, setMinutes, false)}
      </div>
      <p className="date-time-popover__countdown-when">
        {when ? `Starts ${formatCountdownTarget(when)}` : 'Type how long from now'}
      </p>
      {commits && (
        <button
          type="button"
          className="date-time-popover__countdown-set"
          disabled={!when}
          onClick={() => onApply(new Date(Date.now() + totalMinutes * 60 * 1000))}
        >
          {applyLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Shared calendar panel: title, month grid, and optional time.
 * Extra rows (a note, a confirm button) are children. Only reminders pass those.
 * chooseMode opens a date/time vs countdown step first. Countdown turns the
 * typed duration into an absolute date (now + that long).
 * showRecurring adds a third choice used by mission reminders.
 */
const DateTimePopover = forwardRef(function DateTimePopover({
  title,
  selected,
  onChange,
  onClear,
  showClear = false,
  showTimeSelect = false,
  chooseMode = false,
  showRecurring = false,
  initialMode,
  onModeChange,
  recurSeed = null,
  onRecurChange,
  countdownCommits = false,
  countdownApplyLabel = 'Set',
  className = '',
  style,
  onClick,
  onMouseDown,
  onDismiss,
  children,
  ...pickerProps
}, ref) {
  const [mode, setMode] = useState(initialMode || (chooseMode ? 'choose' : 'date'));
  const hasValue = selected != null && selected !== '';
  const showChooser = chooseMode && mode === 'choose';
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const onModeChangeRef = useRef(onModeChange);
  onModeChangeRef.current = onModeChange;

  useEffect(() => {
    onModeChangeRef.current?.(mode);
  }, [mode]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || !dismissRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      ignoreNextTimeCommit();
      dismissRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  return (
    <div
      ref={ref}
      className={[
        'date-time-popover',
        mode === 'recurring' ? 'date-time-popover--recurring' : '',
        className,
      ].filter(Boolean).join(' ')}
      style={style}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      {title ? <h2 className="date-time-popover__title">{title}</h2> : null}
      {chooseMode && mode !== 'choose' && (
        <button
          type="button"
          className="date-time-popover__back"
          onClick={() => setMode('choose')}
        >
          Back
        </button>
      )}
      {showChooser && (
        <div className="date-time-popover__choices">
          <button
            type="button"
            className="date-time-popover__choice"
            onClick={() => setMode('date')}
          >
            <span className="date-time-popover__choice-label">Date & time</span>
            <span className="date-time-popover__choice-hint">Pick a day and a time</span>
          </button>
          <button
            type="button"
            className="date-time-popover__choice"
            onClick={() => {
              setMode('countdown');
              if (!countdownCommits) onChange(null);
            }}
          >
            <span className="date-time-popover__choice-label">Countdown</span>
            <span className="date-time-popover__choice-hint">Type how long from now</span>
          </button>
          {showRecurring && (
            <button
              type="button"
              className="date-time-popover__choice"
              onClick={() => setMode('recurring')}
            >
              <span className="date-time-popover__choice-label">Recurring</span>
              <span className="date-time-popover__choice-hint">Repeat on a schedule</span>
            </button>
          )}
        </div>
      )}
      {mode === 'date' && (
        <DatePicker
          selected={selected}
          onChange={onChange}
          showTimeSelect={showTimeSelect}
          inline
          {...pickerProps}
        />
      )}
      {mode === 'countdown' && (
        <CountdownFields
          commits={countdownCommits}
          applyLabel={countdownApplyLabel}
          onApply={onChange}
        />
      )}
      {mode === 'recurring' && (
        <RecurringFields seed={recurSeed} onChange={onRecurChange} />
      )}
      {mode !== 'choose' && children}
      {mode === 'date' && showClear && hasValue && (
        <div className="date-time-popover__clear-row">
          <button type="button" className="date-time-popover__clear" onClick={onClear}>
            Clear
          </button>
        </div>
      )}
    </div>
  );
});

export default DateTimePopover;
